import axios from "axios";
import firebase from "firebase/app";
import "firebase/database";

const FLUSH_INTERVAL_MS = 60000;
const ENABLED_KEY = "genesis.firebaseUsage.enabled";
const EVENT_TYPE = "firebase_usage_summary";
const MAX_REGULAR_GROUPS_PER_WINDOW = 100;
const OVERFLOW_KEY = "__genesis_usage_overflow__";
const groups = new Map();

let currentPage = normalizePage(window.location.pathname);
let targetPage = null;
let flushTimer = null;
let installed = false;
let axiosInterceptorId = null;

function isEnabled() {
  return window.localStorage.getItem(ENABLED_KEY) !== "false";
}

function normalizePage(value) {
  if (!value) return "/";
  const page = String(value).split("?")[0].split("#")[0];
  return page || "/";
}

function attributedPage() {
  return normalizePage(targetPage || currentPage || window.location.pathname);
}

function utf8Bytes(value) {
  if (value === undefined || value === null) return 0;
  try {
    const text = typeof value === "string" ? value : JSON.stringify(value);
    return new TextEncoder().encode(text || "").length;
  } catch (_) {
    return 0;
  }
}

function databaseNameFromUrl(value) {
  try {
    const hostname = new URL(value).hostname;
    return hostname.split(".")[0] || "unknown";
  } catch (_) {
    return "unknown";
  }
}

function normalizeEndpoint(pathname) {
  const raw = String(pathname || "/")
    .replace(/\.json$/i, "")
    .replace(/^\/+|\/+$/g, "");
  if (!raw) return "/";

  const parts = raw.split("/").map((part) => {
    // Firebase paths frequently contain legajos or other short numeric IDs.
    if (/^\d+$/.test(part)) return "{id}";
    // Realtime Database push IDs are 20-character URL-safe strings prefixed by '-'.
    if (/^-[a-z0-9_-]{19,}$/i.test(part)) return "{id}";
    if (/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(part)) return "{id}";
    if (/^(?=.*\d)(?=.*[a-z_-])[a-z0-9_-]{16,}$/i.test(part)) return "{id}";
    return part;
  });
  return `/${parts.join("/")}`;
}

function firebaseRequest(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    if (!/firebaseio\.com$|firebasedatabase\.app$/i.test(parsed.hostname)) return null;
    return {
      database: databaseNameFromUrl(parsed.href),
      endpoint: normalizeEndpoint(parsed.pathname),
    };
  } catch (_) {
    return null;
  }
}

function referenceDetails(ref) {
  try {
    const url = ref.toString();
    const parsed = new URL(url);
    return {
      database: databaseNameFromUrl(
        (ref.database && ref.database.app && ref.database.app.options.databaseURL) || url
      ),
      endpoint: normalizeEndpoint(parsed.pathname),
    };
  } catch (_) {
    return { database: "unknown", endpoint: "/unknown" };
  }
}

function addMeasurement(measurement) {
  if (!isEnabled()) return;
  const page = measurement.page || attributedPage();
  let key = [
    page,
    measurement.database,
    measurement.endpoint,
    measurement.method,
    measurement.source,
    measurement.listenerType || "",
  ].join("|");

  const isNewRegularGroup = !groups.has(key);
  const useOverflow = isNewRegularGroup && groups.size >= MAX_REGULAR_GROUPS_PER_WINDOW;
  if (useOverflow) key = OVERFLOW_KEY;

  const previous = groups.get(key) || {
    page: useOverflow ? "/__overflow__" : page,
    firebase_database: useOverflow ? "multiple" : measurement.database,
    endpoint: useOverflow ? "/__overflow__" : measurement.endpoint,
    method: useOverflow ? "MIXED" : measurement.method,
    source: useOverflow ? "mixed" : measurement.source,
    listener_type: useOverflow ? null : (measurement.listenerType || null),
    download_bytes: 0,
    request_count: 0,
    callback_count: 0,
    error_count: 0,
    latency_total_ms: 0,
    latency_max_ms: 0,
    last_status: null,
    measurement: "estimated_json_payload",
    overflowed: useOverflow,
    collapsed_measurement_count: 0,
  };

  previous.download_bytes += measurement.bytes || 0;
  previous.request_count += measurement.requests || 0;
  previous.callback_count += measurement.callbacks || 0;
  previous.error_count += measurement.errors || 0;
  if (useOverflow || key === OVERFLOW_KEY) previous.collapsed_measurement_count += 1;
  previous.latency_total_ms += measurement.latency || 0;
  previous.latency_max_ms = Math.max(previous.latency_max_ms, measurement.latency || 0);
  if (measurement.status !== undefined) previous.last_status = measurement.status;
  groups.set(key, previous);
}

function recordSnapshot(ref, eventType, mode, snapshot, startedAt, page) {
  const details = referenceDetails(ref);
  addMeasurement({
    page,
    database: details.database,
    endpoint: details.endpoint,
    method: mode === "once" ? "ONCE" : "LISTENER",
    source: "firebase_sdk",
    listenerType: eventType,
    bytes: utf8Bytes(snapshot && snapshot.val ? snapshot.val() : null),
    requests: mode === "once" ? 1 : 0,
    callbacks: 1,
    latency: Date.now() - startedAt,
    status: 200,
  });
}

function recordSdkError(ref, eventType, mode, startedAt, page) {
  const details = referenceDetails(ref);
  addMeasurement({
    page,
    database: details.database,
    endpoint: details.endpoint,
    method: mode === "once" ? "ONCE" : "LISTENER",
    source: "firebase_sdk",
    listenerType: eventType,
    requests: mode === "once" ? 1 : 0,
    errors: 1,
    latency: Date.now() - startedAt,
  });
}

function installRouterAttribution(router) {
  if (!router) return;
  router.beforeEach((to, _from, next) => {
    targetPage = normalizePage(to && to.path);
    next();
  });
  router.afterEach((to) => {
    currentPage = normalizePage(to && to.path);
    targetPage = null;
  });
  if (router.onError) router.onError(() => { targetPage = null; });
}

function installAxiosMonitoring() {
  axiosInterceptorId = axios.interceptors.request.use((config) => {
    const details = firebaseRequest(config && config.url);
    if (details && isEnabled()) {
      config.__genesisFirebaseUsage = {
        ...details,
        page: attributedPage(),
        startedAt: Date.now(),
      };
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      const tracked = response.config && response.config.__genesisFirebaseUsage;
      if (tracked) {
        addMeasurement({
          page: tracked.page,
          database: tracked.database,
          endpoint: tracked.endpoint,
          method: String(response.config.method || "GET").toUpperCase(),
          source: "firebase_rest",
          bytes: utf8Bytes(response.data),
          requests: 1,
          latency: Date.now() - tracked.startedAt,
          status: response.status,
        });
      }
      return response;
    },
    (error) => {
      const config = error && error.config;
      const tracked = config && config.__genesisFirebaseUsage;
      if (tracked) {
        addMeasurement({
          page: tracked.page,
          database: tracked.database,
          endpoint: tracked.endpoint,
          method: String(config.method || "GET").toUpperCase(),
          source: "firebase_rest",
          requests: 1,
          errors: 1,
          latency: Date.now() - tracked.startedAt,
          status: error.response && error.response.status,
        });
      }
      return Promise.reject(error);
    }
  );
}

function installSdkMonitoring() {
  const Reference = firebase.database && firebase.database.Reference;
  if (!Reference || Reference.prototype.__genesisUsagePatched) return;

  const originalOnce = Reference.prototype.once;
  const originalOn = Reference.prototype.on;
  const originalOff = Reference.prototype.off;
  const callbackMap = new WeakMap();

  Reference.prototype.once = function(eventType, successCallback, failureCallback, context) {
    if (!isEnabled()) return originalOnce.apply(this, arguments);
    const ref = this;
    const startedAt = Date.now();
    const page = attributedPage();
    let success = successCallback;
    let failure = failureCallback;

    if (typeof successCallback === "function") {
      success = function(snapshot) {
        recordSnapshot(ref, eventType, "once", snapshot, startedAt, page);
        return successCallback.apply(this, arguments);
      };
    }
    if (typeof failureCallback === "function") {
      failure = function() {
        recordSdkError(ref, eventType, "once", startedAt, page);
        return failureCallback.apply(this, arguments);
      };
    }

    const result = originalOnce.call(ref, eventType, success, failure, context);
    if (typeof successCallback !== "function" && result && typeof result.then === "function") {
      return result.then((snapshot) => {
        recordSnapshot(ref, eventType, "once", snapshot, startedAt, page);
        return snapshot;
      }).catch((error) => {
        recordSdkError(ref, eventType, "once", startedAt, page);
        throw error;
      });
    }
    return result;
  };

  Reference.prototype.on = function(eventType, callback, cancelCallback, context) {
    if (!isEnabled() || typeof callback !== "function") return originalOn.apply(this, arguments);
    const ref = this;
    const page = attributedPage();
    const startedAt = Date.now();
    const wrapped = function(snapshot) {
      recordSnapshot(ref, eventType, "listener", snapshot, startedAt, page);
      return callback.apply(this, arguments);
    };
    let byEvent = callbackMap.get(callback);
    if (!byEvent) { byEvent = new Map(); callbackMap.set(callback, byEvent); }
    byEvent.set(`${ref.toString()}|${eventType}`, wrapped);
    return originalOn.call(ref, eventType, wrapped, cancelCallback, context);
  };

  Reference.prototype.off = function(eventType, callback, context) {
    let effectiveCallback = callback;
    if (callback && callbackMap.has(callback)) {
      const wrapped = callbackMap.get(callback).get(`${this.toString()}|${eventType}`);
      if (wrapped) effectiveCallback = wrapped;
    }
    return originalOff.call(this, eventType, effectiveCallback, context);
  };

  Object.defineProperty(Reference.prototype, "__genesisUsagePatched", { value: true });
}

function flush() {
  if (!isEnabled() || !groups.size || typeof window.cwr !== "function") return;
  const pending = Array.from(groups.values());
  groups.clear();
  pending.forEach((item) => {
    const divisor = item.request_count || item.callback_count || 1;
    window.cwr("recordEvent", {
      type: EVENT_TYPE,
      data: {
        ...item,
        latency_avg_ms: Math.round(item.latency_total_ms / divisor),
        latency_total_ms: undefined,
      },
    });
  });
}

export function installFirebaseUsageMonitoring(router) {
  if (installed) return;
  installed = true;
  installRouterAttribution(router);
  installAxiosMonitoring();
  installSdkMonitoring();
  flushTimer = window.setInterval(flush, FLUSH_INTERVAL_MS);
  window.addEventListener("pagehide", flush);
  window.__genesisFirebaseUsage = {
    isEnabled,
    pendingGroups: () => groups.size,
    flush,
    version: "1.2.0",
    maxGroupsPerWindow: MAX_REGULAR_GROUPS_PER_WINDOW,
    overflowMeasurements: () => {
      const overflow = groups.get(OVERFLOW_KEY);
      return overflow ? overflow.collapsed_measurement_count : 0;
    },
  };
}

export function uninstallFirebaseUsageMonitoring() {
  if (axiosInterceptorId !== null) axios.interceptors.request.eject(axiosInterceptorId);
  if (flushTimer) window.clearInterval(flushTimer);
  window.removeEventListener("pagehide", flush);
  installed = false;
}
